<template>
  <v-container ref="container" v-scroll="onScroll">
    <TitleParagraphRow :value="value" />

    <v-row
      v-if="isMounted && !hideFilters"
      :style="{ top: `${stickyTop}px` }"
      class="sticky-lg"
      style="z-index: 1"
    >
      <v-col v-if="filtersData">
        <v-card elevation="4" rounded="0" theme="light">
          <v-card-text>
            <v-row align="center">
              <v-col class="text-h4 font-weight-light">
                <v-card
                  class="text-h4 font-weight-light"
                  variant="flat"
                  @click="toggleFilters"
                >
                  <v-icon
                    :icon="mdiTuneVariant"
                    color="grey-darken-1"
                    size="42"
                  />
                  Filters
                </v-card></v-col
              >

              <v-col cols="auto">
                <v-btn variant="text" @click="resetFilters"
                  >Reset Filters</v-btn
                >
                <v-btn
                  :append-icon="filtersShown ? mdiChevronUp : mdiChevronDown"
                  variant="text"
                  @click="toggleFilters"
                  >{{ filtersShown ? 'Hide' : 'Show' }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <v-expand-transition>
            <v-card-text v-if="filtersShown">
              <v-row class="pt-2">
                <v-col
                  v-if="shownFilters.what && (whatMenu || whatProducts)"
                  cols="12"
                  md="6"
                  xl=""
                >
                  <WhatSelect
                    ref="whatSelect"
                    :what-menu="whatMenu"
                    :what-products="whatProducts"
                    @select="selectWhatItem"
                  />
                </v-col>

                <v-col v-if="shownFilters.search" cols="12" md="6" xl="">
                  <v-text-field
                    v-model.trim="filtersData.search"
                    clearable
                    hide-details
                    label="Search Name"
                    variant="outlined"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>

    <TrainerPersonList
      :filters-value="filtersData"
      :limit="!isMounted ? undefined : main ? 108 : xs ? 2 : 12"
      :show-all-button="main"
      :upcoming="upcoming"
    />

    <v-row v-if="!main" class="pt-6">
      <v-col align="center">
        <v-btn
          :append-icon="mdiArrowRight"
          color="secondary"
          href="/trainers"
          variant="text"
        >
          View All Trainers
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useMounted } from '@vueuse/core'
import { MENU_BAR_HEIGHT, FILTER_WHAT_ALL } from '#root/lib/consts'
import { scrollToRef } from '#root/lib/utils'
import debounce from 'lodash.debounce'
import {
  mdiArrowRight,
  mdiChevronDown,
  mdiChevronUp,
  mdiTuneVariant
} from '@mdi/js'

const FILTERS_DEFAULT = {
  search: true,
  what: true
}
const FILTERS_VALUE_DEFAULT = {
  search: '',
  what: FILTER_WHAT_ALL
}

const emit = defineEmits(['update:filtersValue'])

const props = defineProps({
  hideFilters: {
    default: false,
    type: Boolean
  },
  filters: {
    default: undefined,
    type: Object
  },
  filtersValue: {
    default: undefined,
    type: Object
  },
  main: {
    default: false,
    type: Boolean
  },
  stickyTop: {
    default: MENU_BAR_HEIGHT,
    type: Number
  },
  upcoming: {
    default: false,
    type: Boolean
  },
  value: {
    required: true,
    type: Object
  },
  whatMenu: {
    default: undefined,
    type: Object
  },
  whatProducts: {
    default: undefined,
    type: Array
  }
})

const { xs, lgAndUp } = useDisplay()
const isMounted = useMounted()
const container = ref(null)
const filtersData = ref(null)
const filtersShown = ref(true)
const filtersAutoHideEnabled = ref(true)
const filtersOriginal = ref(null)
const shownFilters = computed(() => {
  return Object.assign({}, FILTERS_DEFAULT, props.filters)
})
const whatSelect = ref(null)

/** @type {function | undefined} */
let debounced

function onScroll(e) {
  if (
    lgAndUp.value &&
    filtersAutoHideEnabled.value &&
    filtersShown.value &&
    e.target.documentElement.scrollTop > window.innerHeight / 2
  )
    filtersShown.value = false
}

function resetFilters() {
  filtersData.value = Object.assign({}, filtersOriginal.value)

  if (whatSelect.value && filtersData.value)
    whatSelect.value.selectItem(filtersData.value.what)
}

function selectWhatItem(newWhat) {
  filtersData.value.what = newWhat
}

function toggleFilters() {
  if (typeof debounced === 'function') debounced()

  filtersAutoHideEnabled.value = false
  filtersShown.value = !filtersShown.value
}

onMounted(() => {
  watch(
    filtersData,
    newValue => {
      if (!debounced) {
        debounced = debounce(() => {
          filtersAutoHideEnabled.value = true
        }, 2000)
      } else if (container.value && lgAndUp.value) {
        debounced()
        filtersAutoHideEnabled.value = false

        scrollToRef(container, { behavior: 'instant' })
      }

      emit('update:filtersValue', newValue)
    },
    { deep: true }
  )

  watch(whatSelect, newValue => {
    if (newValue) newValue.selectItem(filtersData.value.what)
  })

  filtersData.value = Object.assign(
    {},
    FILTERS_VALUE_DEFAULT,
    props.filtersValue
  )
  filtersOriginal.value = Object.assign({}, filtersData.value)
})
</script>
